'use client';

import React, { createContext, useContext } from 'react';

import { cn } from '../lib/utils';
import { EXPERIENCE_SURFACE_BASE_CLASS } from '../lib/experience-semantic-styles';

export const EXPERIENCE_RENDER_MODES = [
  'light',
  'dark',
  'high_contrast',
  'reduced_motion',
] as const;
export type ExperienceRenderMode = (typeof EXPERIENCE_RENDER_MODES)[number];

interface ExperienceRenderModeContextValue {
  mode: ExperienceRenderMode;
  governed: boolean;
}

const ExperienceRenderModeContext = createContext<ExperienceRenderModeContextValue>({
  mode: 'light',
  governed: false,
});

export function useExperienceRenderMode(): ExperienceRenderMode {
  return useContext(ExperienceRenderModeContext).mode;
}

export function useExperienceModeBoundary(): ExperienceRenderModeContextValue {
  return useContext(ExperienceRenderModeContext);
}

export function canActivateExperienceEvidenceBoundary(environment: string | undefined): boolean {
  return environment === 'development' || environment === 'test';
}

export interface ExperienceModeBoundaryProps extends React.HTMLAttributes<HTMLDivElement> {
  mode: ExperienceRenderMode;
  applySurface?: boolean;
}

/** Applies the frozen browser render-mode matrix to generated semantic tokens. */
export function ExperienceModeBoundary({
  mode,
  applySurface = true,
  className,
  children,
  ...props
}: ExperienceModeBoundaryProps) {
  const resolvedMode: ExperienceRenderMode = (
    EXPERIENCE_RENDER_MODES as readonly string[]
  ).includes(mode)
    ? mode
    : 'light';
  const evidenceAllowed = canActivateExperienceEvidenceBoundary(process.env.NODE_ENV);
  return (
    <ExperienceRenderModeContext.Provider value={{ mode: resolvedMode, governed: evidenceAllowed }}>
      <div
        {...props}
        className={cn(
          applySurface && 'border border-border bg-surface-base text-text-body',
          applySurface && EXPERIENCE_SURFACE_BASE_CLASS,
          className,
        )}
        data-experience-render-mode={resolvedMode}
        data-experience-brand={evidenceAllowed ? 'true' : undefined}
        data-experience-boundary-refused={evidenceAllowed ? undefined : 'production'}
        data-theme={resolvedMode === 'dark' ? 'dark' : 'light'}
        data-contrast={resolvedMode === 'high_contrast' ? 'high' : undefined}
        data-motion={resolvedMode === 'reduced_motion' ? 'reduced' : 'full'}
        style={{
          colorScheme: resolvedMode === 'dark' ? 'dark' : 'light',
          ...props.style,
        }}
      >
        {children}
      </div>
    </ExperienceRenderModeContext.Provider>
  );
}
